/**
 * Widget Registry Service
 * 
 * Manages widget registration across different namespaces (ui, mobile.ui, dataviz.ui).
 *
 */
import { formatterService } from "./formatter.service";
import type {
    IWidgetEntry,
    IWidgetNamespace,
    IWidgetRegistrationOptions
} from "../models/widget-registry";
import type { WidgetConstructor } from "../models/widget";

// Constants
const STRING = "string";
const FUNCTION = "function";

/**
 * Widget Registry Service implementation
 */
class WidgetRegistryService {
    /**
     * All registered widgets
     */
    private widgets: IWidgetEntry<any>[] = [];

    /**
     * Namespace registers (e.g., { "ui": kendo.ui, "mobile.ui": kendo.mobile.ui })
     */
    private namespaces: Map<string, IWidgetNamespace<any>> = new Map();

    private isWidgetConstructor(value: unknown): value is WidgetConstructor<any> {
        if (typeof value !== FUNCTION || value === null) {
            return false;
        }

        const widget = value as Record<string, unknown>;

        return "fn" in widget && "extend" in widget;
    }

    /**
     * Create a new namespace with roles:{} and register it
     * This is the primary way to create namespaces - the service owns them.
     * @param name - Namespace name (e.g., "ui", "mobile.ui", "dataviz.ui")
     * @param properties - Additional properties to add to the namespace
     * @returns The created namespace object
     */
    createNamespace(name: string, properties?: Record<string, any>): IWidgetNamespace<any> {
        const namespace: IWidgetNamespace<any> = {
            roles: {},
            ...properties
        };
        this.namespaces.set(name, namespace);
        return namespace;
    }

    /**
     * Get a namespace register
     * @param name - Namespace name
     * @returns The namespace object or undefined
     */
    getNamespace(name: string): IWidgetNamespace<any> | undefined {
        return this.namespaces.get(name);
    }

    /**
     * Register a widget using options
     */
    register(widget: WidgetConstructor<any>, options: IWidgetRegistrationOptions = {}): void {
        const namespace = options.namespace || "ui";
        const register = this.namespaces.get(namespace);

        if (!register) {
            throw new Error(`Unknown widget namespace: ${namespace}`);
        }

        this.registerToNamespace(widget, register, options.prefix);
    }

    /**
     * Register a widget to a specific namespace register
     * This is the core implementation matching the original kendo.ui.plugin
     */
    registerToNamespace(widget: WidgetConstructor<any>, register: IWidgetNamespace<any>, prefix?: string): void {
        const name = widget.fn.options.name || (widget as any).options?.name;

        if (!name) {
            throw new Error("Widget must have a name in options");
        }

        prefix = prefix || "";

        const componentName = name.toLowerCase();

        register[name] = widget;
        register.roles[componentName] = widget;

        // Create jQuery plugin
        const getter = "getKendo" + prefix + name;
        const pluginName = "kendo" + prefix + name;

        this.createJQueryPlugin(pluginName, widget, getter);

        // Track widget entry
        const widgetEntry: IWidgetEntry<any> = {
            name: pluginName,
            widget: widget,
            prefix: prefix
        };

        this.widgets.push(widgetEntry);
    }

    /**
     * Get a widget by name
     */
    getWidget(name: string, namespace?: string): WidgetConstructor<any> | undefined {
        if (namespace) {
            const register = this.namespaces.get(namespace);
            const widget = register?.[name];

            return this.isWidgetConstructor(widget) ? widget : undefined;
        }

        // Search all namespaces
        for (const register of this.namespaces.values()) {
            const widget = register[name];

            if (this.isWidgetConstructor(widget)) {
                return widget;
            }
        }

        return undefined;
    }

    /**
     * Get a widget by role name
     */
    getWidgetByRole(role: string, namespace?: string): WidgetConstructor<any> | undefined {
        const lowerRole = role.toLowerCase();

        if (namespace) {
            const register = this.namespaces.get(namespace);
            return register?.roles[lowerRole];
        }

        // Search all namespaces
        for (const register of this.namespaces.values()) {
            if (register.roles[lowerRole]) {
                return register.roles[lowerRole];
            }
        }

        return undefined;
    }

    /**
     * Get all registered widgets
     */
    getAllWidgets(): IWidgetEntry<any>[] {
        return this.widgets.slice(); // Return a copy
    }

    /**
     * Get widget instance from an element
     */
    getWidgetInstance(element: HTMLElement | JQuery, namespace?: IWidgetNamespace<any>): any {
        const el = $(element);
        let result: any;

        const searchNamespaces = namespace ? [namespace] : Array.from(this.namespaces.values());

        for (const ns of searchNamespaces) {
            for (const role in ns.roles) {
                const widget = ns.roles[role];
                const prefix = widget.fn?.options?.prefix || "";
                const dataKey = "kendo" + prefix + (widget.fn?.options?.name || "");
                result = el.data(dataKey);
                if (result) {
                    return result;
                }
            }
        }

        return undefined;
    }

    /**
     * Create jQuery plugin for a widget
     */
    createJQueryPlugin(name: string, widget: WidgetConstructor<any>, getter: string): void {
        const slice = Array.prototype.slice;

        ($.fn as any)[name] = function(this: JQuery, options?: any) {
            let value: any = this;
            let args: any[];

            if (typeof options === STRING) {
                args = slice.call(arguments, 1);

                this.each(function(this: HTMLElement) {
                    const widgetInstance = $.data(this, name);
                    let method: any;
                    let result: any;

                    if (!widgetInstance) {
                        throw new Error(formatterService.format("Cannot call method '{0}' of {1} before it is initialized", options, name));
                    }

                    method = widgetInstance[options];

                    if (typeof method !== FUNCTION) {
                        throw new Error(formatterService.format("Cannot find method '{0}' of {1}", options, name));
                    }

                    result = method.apply(widgetInstance, args);

                    if (result !== undefined) {
                        value = result;
                        return false;
                    }
                });
            } else {
                this.each(function(this: HTMLElement) {
                    return new (widget as any)(this, options);
                });
            }

            return value;
        };

        ($.fn as any)[name].widget = widget;

        ($.fn as any)[getter] = function(this: JQuery) {
            return this.data(name);
        };
    }

    /**
     * Merge roles from multiple namespaces into a single object
     */
    rolesFromNamespaces(namespaces: IWidgetNamespace<any>[], defaultNamespaces?: IWidgetNamespace<any>[]): Record<string, WidgetConstructor<any>> {
        const roles: Record<string, WidgetConstructor<any>>[] = [];
        let idx: number;
        let length: number;

        // Use default namespaces if none provided or first element is falsy
        if (!namespaces[0] && defaultNamespaces) {
            namespaces = defaultNamespaces;
        }

        for (idx = 0, length = namespaces.length; idx < length; idx++) {
            roles[idx] = namespaces[idx].roles;
        }

        const reversedRoles = [...roles].reverse();

        return $.extend.apply(null, ([{}] as any[]).concat(reversedRoles));
    }
}

export const widgetRegistryService = new WidgetRegistryService();